/**
 * Persistent multi-user interests + mutual matches.
 * Separated from matchStore (session self / daily deck / UI).
 *
 * Storage:
 *   un8.matching.interests.v1
 *   un8.matching.mutualMatches.v1
 */

import type { ChatMessage, Interest, MutualMatch } from "./interestTypes";
import {
  expressInterest,
  hasLiked,
  hasPassed,
  hasReverseLike,
  upsertInterestInList,
} from "./interestEngine";
import { pairKey } from "./interestTypes";

import { writeLocal } from "../storage/localWrite";

const INTERESTS_KEY = "un8.matching.interests.v1";
const MATCHES_KEY = "un8.matching.mutualMatches.v1";
const CHATS_KEY = "un8.matching.chats.v1";

type Listener = () => void;
const listeners = new Set<Listener>();
let lastWriteFailed = false;

function notify() {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* ignore */
    }
  }
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  const result = writeLocal(key, JSON.stringify(value));
  lastWriteFailed = !result.ok;
  notify();
}

function loadInterests(): Interest[] {
  return readJson<Interest[]>(INTERESTS_KEY, []);
}

function saveInterests(list: Interest[]) {
  writeJson(INTERESTS_KEY, list);
}

function loadMatches(): MutualMatch[] {
  return readJson<MutualMatch[]>(MATCHES_KEY, []);
}

function saveMatches(list: MutualMatch[]) {
  writeJson(MATCHES_KEY, list);
}

function loadChats(): ChatMessage[] {
  return readJson<ChatMessage[]>(CHATS_KEY, []);
}

function saveChats(list: ChatMessage[]) {
  writeJson(CHATS_KEY, list);
}

export const interestStore = {
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  writeFailed(): boolean {
    return lastWriteFailed;
  },

  listInterests(): Interest[] {
    return loadInterests();
  },

  listMatches(): MutualMatch[] {
    return loadMatches();
  },

  hasLiked(fromUserId: string, toUserId: string): boolean {
    return hasLiked(loadInterests(), fromUserId, toUserId);
  },

  hasPassed(fromUserId: string, toUserId: string): boolean {
    return hasPassed(loadInterests(), fromUserId, toUserId);
  },

  hasReverseLike(fromUserId: string, toUserId: string): boolean {
    return hasReverseLike(loadInterests(), fromUserId, toUserId);
  },

  /**
   * Persist one-way interest and create mutual match when counter-like exists.
   * Pure decision via interestEngine; this layer only persists.
   */
  express(
    fromUserId: string,
    toUserId: string,
    status: "liked" | "passed",
    valuesScore?: number
  ) {
    const interests = loadInterests();
    const matches = loadMatches();
    const result = expressInterest({
      fromUserId,
      toUserId,
      status,
      existingInterests: interests,
      existingMatches: matches,
      valuesScore,
    });

    saveInterests(upsertInterestInList(interests, result.interest));

    if (result.match && result.mutual) {
      const key = pairKey(result.match.userIds[0], result.match.userIds[1]);
      const without = matches.filter(
        (m) => pairKey(m.userIds[0], m.userIds[1]) !== key
      );
      saveMatches([result.match, ...without]);
    }

    return result;
  },

  /**
   * Ensure a like edge exists (e.g. seed demo inbound interest).
   * Does not create matches by itself unless reverse already liked.
   */
  ensureLike(fromUserId: string, toUserId: string): void {
    if (fromUserId === toUserId) return;
    if (hasLiked(loadInterests(), fromUserId, toUserId)) return;
    this.express(fromUserId, toUserId, "liked");
  },

  /** Active matches involving this user. */
  matchesFor(userId: string): MutualMatch[] {
    return loadMatches().filter(
      (m) =>
        m.status === "active" &&
        (m.userIds[0] === userId || m.userIds[1] === userId)
    );
  },

  getMatch(matchId: string): MutualMatch | null {
    return loadMatches().find((m) => m.id === matchId) ?? null;
  },

  unlockChat(matchId: string): MutualMatch | null {
    const matches = loadMatches();
    const idx = matches.findIndex((m) => m.id === matchId && m.status === "active");
    if (idx < 0) return null;
    const updated: MutualMatch = { ...matches[idx], chatUnlocked: true };
    const next = matches.slice();
    next[idx] = updated;
    saveMatches(next);
    return updated;
  },

  listMessages(matchId: string): ChatMessage[] {
    return loadChats()
      .filter((m) => m.matchId === matchId)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  },

  sendMessage(matchId: string, fromUserId: string, body: string): ChatMessage | null {
    const match = this.getMatch(matchId);
    if (!match || match.status !== "active") return null;
    if (!match.chatUnlocked) return null;
    if (match.userIds[0] !== fromUserId && match.userIds[1] !== fromUserId) {
      return null;
    }
    const trimmed = body.trim();
    if (!trimmed) return null;
    const msg: ChatMessage = {
      id: `msg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
      matchId,
      fromUserId,
      body: trimmed.slice(0, 2000),
      createdAt: new Date().toISOString(),
    };
    saveChats([...loadChats(), msg]);
    return msg;
  },

  mergeRemoteMessages(messages: ChatMessage[]): void {
    if (messages.length === 0) return;
    const existing = loadChats();
    const ids = new Set(existing.map((m) => m.id));
    const added = messages.filter((m) => !ids.has(m.id));
    if (added.length === 0) return;
    saveChats([...existing, ...added]);
  },

  /**
   * Clear interests/matches for one user (reset demo session).
   * Does not wipe the entire global store for other users.
   */
  clearForUser(userId: string): void {
    saveInterests(
      loadInterests().filter(
        (i) => i.fromUserId !== userId && i.toUserId !== userId
      )
    );
    const removedMatchIds = new Set(
      loadMatches()
        .filter((m) => m.userIds[0] === userId || m.userIds[1] === userId)
        .map((m) => m.id)
    );
    saveMatches(
      loadMatches().filter(
        (m) => m.userIds[0] !== userId && m.userIds[1] !== userId
      )
    );
    if (removedMatchIds.size > 0) {
      saveChats(loadChats().filter((c) => !removedMatchIds.has(c.matchId)));
    }
  },

  /**
   * Import a mutual match from the server without re-running expressInterest.
   * Keeps local un8.matching.* keys as a cache of remote state.
   */
  importServerMatch(input: {
    matchId: string;
    userIds: [string, string];
    createdAt: string;
    valuesScore: number;
  }): void {
    const matches = loadMatches();
    const key = pairKey(input.userIds[0], input.userIds[1]);
    const without = matches.filter(
      (m) => pairKey(m.userIds[0], m.userIds[1]) !== key
    );
    const match: MutualMatch = {
      id: input.matchId,
      userIds: input.userIds,
      createdAt: input.createdAt,
      status: "active",
      valuesScore: input.valuesScore,
      chatUnlocked: true,
    };
    saveMatches([match, ...without]);

    const interests = loadInterests();
    let next = interests;
    for (const [from, to] of [
      [input.userIds[0], input.userIds[1]],
      [input.userIds[1], input.userIds[0]],
    ] as const) {
      if (!hasLiked(next, from, to)) {
        next = upsertInterestInList(next, {
          id: `int_${from}_${to}`,
          fromUserId: from,
          toUserId: to,
          status: "liked",
          createdAt: input.createdAt,
        });
      }
    }
    saveInterests(next);
  },

  clearAll(): void {
    saveInterests([]);
    saveMatches([]);
    saveChats([]);
  },

  /**
   * Migrate legacy MatchState likedIds/passedIds/matches into multi-user store.
   * Safe to call repeatedly (idempotent upserts).
   */
  migrateFromLegacySession(input: {
    selfId: string;
    likedIds: string[];
    passedIds: string[];
    matches: { profileId: string; matchedAt: string; valuesScore: number; id: string }[];
  }): void {
    const interests = loadInterests();
    let next = interests;
    const now = new Date().toISOString();

    for (const to of input.likedIds) {
      if (!hasLiked(next, input.selfId, to)) {
        next = upsertInterestInList(next, {
          id: `mig_int_${input.selfId}_${to}`,
          fromUserId: input.selfId,
          toUserId: to,
          status: "liked",
          createdAt: now,
        });
      }
    }
    for (const to of input.passedIds) {
      if (!hasPassed(next, input.selfId, to) && !hasLiked(next, input.selfId, to)) {
        next = upsertInterestInList(next, {
          id: `mig_pass_${input.selfId}_${to}`,
          fromUserId: input.selfId,
          toUserId: to,
          status: "passed",
          createdAt: now,
        });
      }
    }
    saveInterests(next);

    const matches = loadMatches();
    const nextMatches = [...matches];
    for (const m of input.matches) {
      const key = pairKey(input.selfId, m.profileId);
      if (nextMatches.some((x) => pairKey(x.userIds[0], x.userIds[1]) === key)) {
        continue;
      }
      const userIds =
        input.selfId < m.profileId
          ? ([input.selfId, m.profileId] as [string, string])
          : ([m.profileId, input.selfId] as [string, string]);
      nextMatches.push({
        id: m.id || `mig_match_${key}`,
        userIds,
        createdAt: m.matchedAt || now,
        status: "active",
        valuesScore: m.valuesScore ?? 0,
      });
      // Ensure both like edges exist for true mutual semantics
      this.ensureLike(input.selfId, m.profileId);
      this.ensureLike(m.profileId, input.selfId);
    }
    saveMatches(nextMatches);
  },
};
